import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import type { UIVisibleComponents, Location, DeliveryAddressDistance, Call, CustomerAddress, PaymentType } from '../../types';

interface CustomerData {
  id: number;
  addresses: CustomerAddress[];
  [key: string]: unknown;
}

export interface OrderFormFields {
  address: string;
  floor: string;
  name: string;
  contact_phone: string;
  extra: string;
  orderNote: string;
}

export interface DeliveryColumnConfig {
  key: string;
  visible: boolean;
}

interface UIState {
  recordsDate: string;
  uiVisibleComponents: UIVisibleComponents;
  activeAgent: string | null;
  loadedCustomer: CustomerData | null;
  loadedCall: Call | null;
  selectedLocation: Location | null;
  selectedDeliveryType: string | null;
  selectedPaymentType: string | null;
  selectedDiscountValue: number | null;
  loadedCustomerAddressIndex: number;
  loadedCustomerAddressCount: number | null;
  modalCustomerDataLoad: boolean;
  handoffLocation: number | null;
  comboEnabled: boolean;
  combo5Enabled: boolean;
  gratisEnabled: boolean;
  deliveryAddressDistance: DeliveryAddressDistance | null;
  orderDelayTime: string | null;
  orderFormFields: OrderFormFields | null;
  // Identifies which loaded customer/address has already been written into the
  // order form. Lets OrderForm populate fields when a new customer or address
  // is loaded (call answer, address switch) without re-clobbering operator
  // edits on a plain tab remount. Survives navigation because it lives here.
  appliedAddressKey: string | null;
  discountLevels: number[];
  localCountryPrefix: string;
  currency: string;
  deliveryModulo: number | null;
  deliveryColumns: DeliveryColumnConfig[] | null;
  userId: number | null;
  onlineOrdersPaused: boolean;
  minDeliveryTime: number | null;
  showMinDeliveryTime: boolean;
  reservationsEnabled: boolean;
  pendingReservationCount: number;
  showDeliverymanStats: boolean;
  allowNonAdminAddDeliverymen: boolean;
  showScannerIndicator: boolean;
}

const initialState: UIState = {
  recordsDate: dayjs().format('YYYY-MM-DD'),
  uiVisibleComponents: {
    ui_delay: false,
    ui_gratis: true,
    ui_nth_gratis: false,
    ui_loyalty: false,
    ui_mini_stats: false,
    ui_distance: false,
    ui_discount: true,
    ui_payment_opts: false,
    ui_map_overlay: false,
    ui_call_stats: false,
    ui_tablet_view: false,
  },
  activeAgent: null,
  loadedCustomer: null,
  loadedCall: null,
  selectedLocation: null,
  selectedDeliveryType: null,
  selectedPaymentType: 'cache',
  selectedDiscountValue: null,
  loadedCustomerAddressIndex: 1,
  loadedCustomerAddressCount: null,
  modalCustomerDataLoad: false,
  handoffLocation: null,
  comboEnabled: false,
  combo5Enabled: false,
  gratisEnabled: false,
  deliveryAddressDistance: null,
  orderDelayTime: null,
  orderFormFields: null,
  appliedAddressKey: null,
  discountLevels: [0, 5, 10, 15, 20],
  localCountryPrefix: '',
  currency: 'EUR',
  deliveryModulo: null,
  deliveryColumns: null,
  userId: null,
  onlineOrdersPaused: false,
  minDeliveryTime: null,
  showMinDeliveryTime: false,
  reservationsEnabled: false,
  pendingReservationCount: 0,
  showDeliverymanStats: false,
  allowNonAdminAddDeliverymen: false,
  showScannerIndicator: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setRecordsDate(state, action: PayloadAction<string>) {
      state.recordsDate = action.payload;
    },
    setVisibleComponents(state, action: PayloadAction<UIVisibleComponents>) {
      state.uiVisibleComponents = action.payload;
      state.comboEnabled = !!action.payload.ui_nth_gratis;
    },
    setActiveAgent(state, action: PayloadAction<string>) {
      state.activeAgent = action.payload;
    },
    setLoadedCustomer(state, action: PayloadAction<CustomerData>) {
      state.loadedCustomer = action.payload;
    },
    setLoadedCall(state, action: PayloadAction<Call>) {
      state.loadedCall = action.payload;
    },
    selectOrderLocation(state, action: PayloadAction<Location | null>) {
      state.selectedLocation = action.payload;
    },
    selectDeliveryType(state, action: PayloadAction<string | null>) {
      state.selectedDeliveryType = action.payload;
    },
    selectPaymentType(state, action: PayloadAction<string | null>) {
      state.selectedPaymentType = action.payload;
    },
    selectDiscountValue(state, action: PayloadAction<number | null>) {
      state.selectedDiscountValue = action.payload;
    },
    setCustomerAddressIndex(state, action: PayloadAction<number>) {
      state.loadedCustomerAddressIndex = action.payload;
    },
    setCustomerAddressCount(state, action: PayloadAction<number>) {
      state.loadedCustomerAddressCount = action.payload;
    },
    openModalCustomerDataLoad(state) {
      state.modalCustomerDataLoad = true;
    },
    closeModalCustomerDataLoad(state) {
      state.modalCustomerDataLoad = false;
    },
    loadPendingCallData(state) {
      // Reset current customer state before loading pending call data
      state.loadedCustomer = null;
      state.loadedCall = null;
      state.selectedLocation = null;
      state.selectedDeliveryType = null;
      state.selectedPaymentType = 'cache';
      state.selectedDiscountValue = null;
      state.loadedCustomerAddressIndex = 1;
      state.loadedCustomerAddressCount = null;
      state.deliveryAddressDistance = null;
      state.comboEnabled = !!state.uiVisibleComponents.ui_nth_gratis;
      state.combo5Enabled = initialState.combo5Enabled;
      state.gratisEnabled = initialState.gratisEnabled;
      state.orderDelayTime = null;
      state.orderFormFields = null;
      state.appliedAddressKey = null;
      state.modalCustomerDataLoad = false;
    },
    setHandoffLocation(state, action: PayloadAction<number | null>) {
      state.handoffLocation = action.payload;
    },
    toggleCombo(state) {
      state.comboEnabled = !state.comboEnabled;
      // Combo5 is a modifier of Combo — disabling Combo should clear it.
      if (!state.comboEnabled) state.combo5Enabled = false;
    },
    toggleCombo5(state) {
      state.combo5Enabled = !state.combo5Enabled;
    },
    toggleGratis(state) {
      state.gratisEnabled = !state.gratisEnabled;
    },
    setDeliveryAddressDistance(state, action: PayloadAction<DeliveryAddressDistance | null>) {
      state.deliveryAddressDistance = action.payload;
    },
    clearDeliveryAddressDistance(state) {
      state.deliveryAddressDistance = null;
    },
    setOrderDelayTime(state, action: PayloadAction<string | null>) {
      state.orderDelayTime = action.payload;
    },
    setOrderFormFields(state, action: PayloadAction<OrderFormFields | null>) {
      state.orderFormFields = action.payload;
    },
    setAppliedAddressKey(state, action: PayloadAction<string | null>) {
      state.appliedAddressKey = action.payload;
    },
    setDiscountLevels(state, action: PayloadAction<number[]>) {
      state.discountLevels = action.payload;
    },
    setLocalCountryPrefix(state, action: PayloadAction<string>) {
      state.localCountryPrefix = action.payload;
    },
    setCurrency(state, action: PayloadAction<string>) {
      state.currency = action.payload;
    },
    setDeliveryModulo(state, action: PayloadAction<number | null>) {
      state.deliveryModulo = action.payload;
    },
    setDeliveryColumns(state, action: PayloadAction<DeliveryColumnConfig[] | null>) {
      state.deliveryColumns = action.payload;
    },
    setUserId(state, action: PayloadAction<number | null>) {
      state.userId = action.payload;
    },
    setOnlineOrdersPaused(state, action: PayloadAction<boolean>) {
      state.onlineOrdersPaused = action.payload;
    },
    setMinDeliveryTime(state, action: PayloadAction<number | null>) {
      state.minDeliveryTime = action.payload;
    },
    setShowMinDeliveryTime(state, action: PayloadAction<boolean>) {
      state.showMinDeliveryTime = action.payload;
    },
    setReservationsEnabled(state, action: PayloadAction<boolean>) {
      state.reservationsEnabled = action.payload;
    },
    setPendingReservationCount(state, action: PayloadAction<number>) {
      state.pendingReservationCount = action.payload;
    },
    setShowDeliverymanStats(state, action: PayloadAction<boolean>) {
      state.showDeliverymanStats = action.payload;
    },
    setAllowNonAdminAddDeliverymen(state, action: PayloadAction<boolean>) {
      state.allowNonAdminAddDeliverymen = action.payload;
    },
    setShowScannerIndicator(state, action: PayloadAction<boolean>) {
      state.showScannerIndicator = action.payload;
    },
    resetCustomer(state) {
      state.loadedCustomer = null;
      state.loadedCall = null;
      state.selectedLocation = null;
      state.selectedDeliveryType = null;
      state.selectedPaymentType = 'cache';
      state.selectedDiscountValue = null;
      state.loadedCustomerAddressIndex = 1;
      state.loadedCustomerAddressCount = null;
      state.deliveryAddressDistance = null;
      state.comboEnabled = !!state.uiVisibleComponents.ui_nth_gratis;
      state.combo5Enabled = initialState.combo5Enabled;
      state.gratisEnabled = initialState.gratisEnabled;
      state.orderDelayTime = null;
      state.orderFormFields = null;
      state.appliedAddressKey = null;
    },
  },
});

export const {
  setRecordsDate,
  setVisibleComponents, setActiveAgent,
  setLoadedCustomer, setLoadedCall,
  selectOrderLocation, selectDeliveryType, selectPaymentType, selectDiscountValue,
  setCustomerAddressIndex, setCustomerAddressCount,
  openModalCustomerDataLoad, closeModalCustomerDataLoad, loadPendingCallData,
  setHandoffLocation,
  toggleCombo, toggleCombo5, toggleGratis,
  setDeliveryAddressDistance, clearDeliveryAddressDistance,
  setOrderDelayTime, setOrderFormFields, setAppliedAddressKey,
  setDiscountLevels,
  setLocalCountryPrefix,
  setCurrency,
  setDeliveryModulo,
  setDeliveryColumns,
  setUserId,
  setOnlineOrdersPaused,
  setMinDeliveryTime,
  setShowMinDeliveryTime,
  setReservationsEnabled,
  setPendingReservationCount,
  setShowDeliverymanStats,
  setAllowNonAdminAddDeliverymen,
  setShowScannerIndicator,
  resetCustomer,
} = uiSlice.actions;

export default uiSlice.reducer;
